import {
  Box,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import { SxProps, Theme, useTheme } from "@mui/material/styles";
import { makeStyles } from "helpers/StyleHelper";
import useBoolean from "hooks/useBoolean";
import React, { useCallback, useEffect, useState } from "react";
import {
  DragDropContext,
  Draggable,
  DropResult,
  Droppable,
} from "react-beautiful-dnd";
import { useIntl } from "react-intl";
import { ContextMenuItem } from "types/common-types";
import EnhancedTableHead from "./EnhancedTableHead";
import EnhancedTableRow from "./EnhancedTableRow";
import EnhancedTableToolbar from "./EnhancedTableToolbar";

export const MAX_ROWS_BEFORE_PAGINATION = 10;
export const DEFAULT_ROWS_PER_PAGE = 10;

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    width: "100%",
  },
  paper: {
    width: "100%",
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("xs")]: {
      marginLeft: theme.spacing(-2),
      marginRight: theme.spacing(-2),
      width: "100vw",
      boxShadow: "none",
    },
  },
  noResults: {
    border: "none",
    textAlign: "center",
    padding: "2em 0",
    fontStyle: "oblique",
  },
}));

export type LazyTableRow = {
  id: string;
  color?: string;
  isDragDisabled?: boolean;
  tooltip?: string;
};

export type TableSortType = "desc" | "asc";

export type BasicCellType = {
  accessor: string;
  customSortAccessor?: string;
  label: string;
  align?: "left" | "center" | "right";
  type: "text" | "node" | "boolean" | "date" | "datetime" | "price";
  className?: string;
  width?: any;
  noSort?: boolean;
};

export type BooleanCellType = BasicCellType & {
  type: "boolean";
  TrueComponent?: React.ReactNode;
  FalseComponent?: React.ReactNode;
};

export type NodeCellType = BasicCellType & {
  type: "node";
  View: React.FC<{ value: any }>;
};

export type TableCellType = BasicCellType | BooleanCellType | NodeCellType;

export type LazyTableProps = {
  topComp?: React.ReactNode;
  title?: string;
  titleComponent?: React.ReactNode;
  cells: Array<TableCellType>;
  rows: Array<LazyTableRow>;
  rowCount: number;
  loading?: boolean;
  allowPagination?: boolean;
  page: number;
  setPage: (page: number) => void;
  rowsPerPage: number;
  setRowsPerPage: (rowsPerPage: number) => void;
  order: TableSortType;
  setOrder: (direction: TableSortType) => void;
  orderBy: string;
  setOrderBy: (column: string) => void;
  dense?: boolean;
  className?: string;
  allowDragAndDrop?: boolean;
  onDragAndDropEnd?: (result: DropResult) => void;
  onEdit?: (id: string) => void;
  onClick?: (id: string, row: any) => void;
  onDelete?: (ids: string[]) => void;
  executeMultiple?: {
    dialogLabel: string;
    onExecuteMultiple: (ids: string[]) => Promise<void>;
    buttonLabel: string;
    icon?: React.ReactNode;
  };
  useRowIndexesAsReactKey?: boolean;
  stickyHeader?: boolean;
  maxHeight?: number;
  contextMenuItems?: ContextMenuItem[];
  noResultsLabel?: string;
  /** Style overrides applied per row, merged over the row's background color. */
  rowSx?: (row: LazyTableRow) => SxProps<Theme>;
};

const LazyTable = ({
  topComp,
  title,
  titleComponent,
  cells,
  loading,
  rows,
  rowCount,
  allowPagination = true,
  page,
  setPage,
  order,
  setOrder,
  orderBy,
  setOrderBy,
  rowsPerPage,
  setRowsPerPage,
  dense,
  className,
  allowDragAndDrop,
  onDragAndDropEnd,
  onEdit,
  onDelete,
  onClick,
  executeMultiple,
  useRowIndexesAsReactKey,
  stickyHeader,
  maxHeight,
  contextMenuItems,
  noResultsLabel,
  rowSx,
}: LazyTableProps) => {
  const { formatMessage: t } = useIntl();
  const styles = useStyles(useTheme());
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [dragging, setDragging] = useBoolean(false);
  const [dragStarting, dragStartingActions] = useBoolean(false);
  const [data, setData] = useState<LazyTableRow[]>([]);
  const handleRequestSort = (event: any, property: string) => {
    const isDesc = orderBy === property && order === "desc";
    setOrder!(isDesc ? "asc" : "desc");
    setOrderBy!(property);
  };

  useEffect(() => {
    setData(rows);
  }, [rows]);

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelecteds = rows.map((n) => n.id);
      setSelectedIds(newSelecteds);
      return;
    }
    setSelectedIds([]);
  };

  const innerOnDelete = (ids: string[]) => {
    if (onDelete) onDelete(ids);
    setSelectedIds([]);
  };

  const innerOnExecuteMultiple = async (ids: string[]): Promise<void> => {
    if (executeMultiple?.onExecuteMultiple)
      await executeMultiple?.onExecuteMultiple(ids);
    setSelectedIds([]);
  };

  const handleClick = useCallback(
    (event: any, id: string, row: LazyTableRow) => {
      if (onClick && event.target.type !== "checkbox" && !event.target.href)
        onClick(id, row);
      if (!onEdit && !onDelete && !executeMultiple?.onExecuteMultiple) return;
      const selectedIndex = selectedIds.indexOf(id);
      const newSelected =
        selectedIndex === -1
          ? [...selectedIds, id]
          : selectedIds.filter((selectedId) => selectedId !== id);

      setSelectedIds(newSelected);
    },
    [onClick, onDelete, onEdit, executeMultiple?.onExecuteMultiple, selectedIds]
  );

  const handleChangePage = (event: any, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const isSelected = useCallback(
    (id: string) => selectedIds.indexOf(id) !== -1,
    [selectedIds]
  );

  const reorder = (
    list: LazyTableRow[],
    startIndex: number,
    endIndex: number
  ) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
  };

  const onDragEnd = useCallback(
    async (result: any) => {
      dragStartingActions.setFalse();
      // the only one that is required
      if (!result.destination) {
        return;
      }

      setData((prev) =>
        reorder(prev, result.source.index, result.destination.index)
      );

      try {
        onDragAndDropEnd && (await onDragAndDropEnd(result));
      } catch (error) {
        console.log(error);
      } finally {
        setDragging.setFalse();
      }
    },
    [onDragAndDropEnd, setDragging, dragStartingActions]
  );

  const noResults = noResultsLabel && !rows.length && !loading;

  return (
    <Box className={className} sx={styles.root}>
      <Paper sx={styles.paper}>
        {topComp}
        {(title || titleComponent) && (
          <EnhancedTableToolbar
            title={title}
            titleComponent={titleComponent}
            selectedIds={selectedIds}
            onEdit={onEdit}
            onDelete={innerOnDelete}
            executeMultiple={executeMultiple && {
              onExecuteMultiple: innerOnExecuteMultiple,
              buttonLabel: executeMultiple.buttonLabel,
              dialogLabel: executeMultiple.dialogLabel,
              icon: executeMultiple.icon
            }}
          />
        )}
        {(dragging || loading) && (
          <LinearProgress
            style={{ width: "100%" }}
            color="primary"
            variant="query"
          />
        )}

        <TableContainer
          style={maxHeight ? { maxHeight: maxHeight } : undefined}
        >
          <Table stickyHeader={stickyHeader} size={dense ? "small" : "medium"}>
            <EnhancedTableHead
              cells={cells}
              numSelected={selectedIds.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={onDelete && handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={rowCount}
              withCheckbox={!!executeMultiple?.onExecuteMultiple}
            />
            <DragDropContext
              onDragEnd={onDragEnd}
              onBeforeDragStart={dragStartingActions.setTrue}
            >
              <Droppable
                droppableId="droppable-1"
                isDropDisabled={dragging || !allowDragAndDrop}
              >
                {(provided, snapshot) => (
                  <TableBody
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                  >
                    {noResults && (
                      <TableRow>
                        <TableCell colSpan={cells.length} sx={styles.noResults}>
                          <Typography>{noResultsLabel}</Typography>
                        </TableCell>
                      </TableRow>
                    )}

                    {data.map((row, index) => (
                      <Draggable
                        key={useRowIndexesAsReactKey ? index : row.id}
                        draggableId={row.id}
                        index={index}
                        isDragDisabled={
                          row.isDragDisabled || dragging || !allowDragAndDrop
                        }
                      >
                        {(provided, snapshot) => (
                          <EnhancedTableRow
                            key={useRowIndexesAsReactKey ? index : row.id}
                            index={index}
                            row={row}
                            hover={!!(onEdit || onDelete || onClick)}
                            cells={cells}
                            withCheckbox={
                              !!onDelete ||
                              !!onEdit ||
                              !!executeMultiple?.onExecuteMultiple
                            }
                            handleClick={handleClick}
                            isSelected={isSelected}
                            innerRef={provided.innerRef}
                            dragStarting={dragStarting}
                            draggableProps={provided.draggableProps}
                            dragHandleProps={provided.dragHandleProps}
                            contextMenuItems={contextMenuItems}
                            sx={rowSx?.(row)}
                          />
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </TableBody>
                )}
              </Droppable>
            </DragDropContext>
          </Table>
        </TableContainer>

        {allowPagination && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            component="div"
            count={rowCount}
            rowsPerPage={rowsPerPage!}
            page={page!}
            labelRowsPerPage={t({ id: "tableRowsPerPage" })}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      </Paper>
    </Box>
  );
};

export default LazyTable;
